import { useEffect, useRef, useState } from "react";
import { api, errText, mediaUrl } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { Avatar, CreatorName, Loading, Empty, formatNum } from "./common";
import { PostCard } from "./PostCard";
import { toast } from "sonner";
import { ArrowLeft, Send, X, UserPlus, UserCheck, MessageSquare, Grid3x3 } from "lucide-react";

// Live DM thread: websocket push (message / typing / read) with a 3 s "since" poll as fallback inside the FiveM browser.
export function PhoneThread({ user, conv, onBack }) {
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [typingAt, setTypingAt] = useState(0);
  const [, tick] = useState(0);
  const endRef = useRef();
  const lastTyped = useRef(0);
  const lastAt = useRef(null);
  const pollN = useRef(0);
  const lastMineUnread = useRef(false);

  const merge = (rows) => {
    if (!rows?.length) return;
    setMessages((cur) => {
      const map = new Map((cur || []).map((m) => [m.id, m]));
      rows.forEach((m) => map.set(m.id, m));
      const out = [...map.values()].sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
      lastAt.current = out[out.length - 1]?.created_at;
      return out;
    });
  };
  const poll = () => api.get(`/conversations/${conv.id}/messages`, { params: lastAt.current ? { since: lastAt.current } : {} })
    .then(({ data }) => { if (!lastAt.current) setMessages(data); merge(data); if (!data.length && !lastAt.current) setMessages([]); }).catch(() => {});
  useEffect(() => {
    lastAt.current = null; setMessages(null); poll();
    const i = setInterval(() => {
      pollN.current += 1;
      // Fallback for the FiveM browser without websockets: every 2nd poll refetch the full thread while my last message is unread.
      if (pollN.current % 2 === 0 && lastMineUnread.current) api.get(`/conversations/${conv.id}/messages`).then(({ data }) => merge(data)).catch(() => {});
      else poll();
      api.get(`/conversations/${conv.id}/typing`).then(({ data }) => { if (data.typing.length) setTypingAt(Date.now()); }).catch(() => {});
    }, 3000);
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => { clearInterval(i); clearInterval(t); };
  }, [conv.id]); // eslint-disable-line
  useRealtime((msg) => {
    const d = msg?.data || {};
    if (d.conversation_id !== conv.id) return;
    if (msg.event === "message") { merge([d]); setTypingAt(0); poll(); }
    if (msg.event === "typing" && d.user_id !== user.id) setTypingAt(Date.now());
    if (msg.event === "read") setMessages((cur) => (cur || []).map((m) => (m.sender_id === user.id ? { ...m, read: true } : m)));
  });
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typingAt]);

  const onType = (v) => {
    setText(v);
    if (v && Date.now() - lastTyped.current > 2500) { lastTyped.current = Date.now(); api.post(`/conversations/${conv.id}/typing`).catch(() => {}); }
  };
  const send = async () => {
    if (!text.trim()) return; const t = text; setText(""); lastTyped.current = 0;
    try { const { data } = await api.post("/messages", { conversation_id: conv.id, text: t }); merge([data]); }
    catch (e) { toast.error(errText(e)); setText(t); }
  };
  const isTyping = Date.now() - typingAt < 5000;
  const lastMine = [...(messages || [])].reverse().find((m) => m.sender_id === user.id);
  lastMineUnread.current = !!(lastMine && !lastMine.read);
  return (
    <div className="flex flex-col h-full" data-testid="sl-thread">
      <div className="flex items-center gap-2 px-3 h-12 border-b border-white/10 shrink-0">
        <button onClick={onBack} data-testid="sl-thread-back"><ArrowLeft size={18} /></button>
        <Avatar src={conv.other?.avatar_url} name={conv.other?.display_name} size={30} />
        <div className="min-w-0"><div className="text-sm font-semibold truncate">{conv.other?.display_name}</div>
          <div className="text-[10px] text-muted-foreground" data-testid="sl-thread-status">{isTyping ? <span className="text-eblue">typing…</span> : `@${conv.other?.username}`}</div></div>
      </div>
      <div className="flex-1 overflow-y-auto hide-scroll p-3 space-y-2">
        {!messages ? <Loading /> : messages.map((m) => { const mine = m.sender_id === user.id; return (
          <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"} animate-rise`} data-testid={`sl-msg-${m.id}`}>
            <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${mine ? "btn-blue" : "surface-2"}`}>
              {m.text && <span className="whitespace-pre-wrap break-words">{m.text}</span>}
              {m.media_url && <img src={mediaUrl(m.media_url)} className="rounded-lg mt-1 max-h-48" alt="" />}
            </div>
            {mine && lastMine?.id === m.id && <span className="text-[10px] text-muted-foreground mt-0.5" data-testid="sl-msg-receipt">{m.read ? "Seen" : "Sent"}</span>}
          </div>
        ); })}
        {isTyping && <div className="flex" data-testid="sl-typing-indicator"><div className="surface-2 rounded-2xl px-3 py-2.5 flex gap-1">{[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}</div></div>}
        <div ref={endRef} />
      </div>
      <div className="flex items-center gap-2 px-2 py-2 border-t border-white/10 shrink-0">
        <input value={text} onChange={(e) => onType(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message…" className="flex-1 surface-2 border border-white/10 rounded-full px-4 h-10 text-sm outline-none" data-testid="sl-msg-input" />
        <button onClick={send} className="w-10 h-10 rounded-full btn-blue flex items-center justify-center shrink-0" data-testid="sl-msg-send"><Send size={16} /></button>
      </div>
    </div>
  );
}

const RESERVED = new Set(["feed", "explore", "music", "tv", "messages", "notifications", "settings", "virtucity", "vstreets", "city", "events", "login", "signup", "admin", "store", "marketplace", "wallet", "live", "reels", "search", "crews", "studio", "dashboard", "terms", "privacy", "awards", "jobs"]);
// Intercepts profile links (/<username>) inside the phone so they open in-app instead of leaving to the full site.
export function profileFromClick(e) {
  const a = e.target.closest?.("a[href]");
  if (!a) return null;
  let u;
  try { u = new URL(a.href, window.location.origin); } catch { return null; }
  if (u.origin !== window.location.origin) return null;
  const m = u.pathname.match(/^\/@?([A-Za-z0-9_.]{2,30})\/?$/);
  return m && !RESERVED.has(m[1].toLowerCase()) ? m[1] : null;
}

export function UserProfileSheet({ username, me, onClose, onMessage, onSignin }) {
  const [u, setU] = useState(null);
  const [posts, setPosts] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setU(null); setPosts(null);
    api.get(`/users/${username}`).then(({ data }) => { setU(data); return api.get(`/users/${data.id}/posts`); })
      .then(({ data }) => setPosts(Array.isArray(data) ? data : data.posts || [])).catch((e) => { if (!u) { toast.error(errText(e)); onClose(); } });
  }, [username]); // eslint-disable-line
  const follow = async () => {
    if (!me) return onSignin();
    setBusy(true);
    try {
      if (u.is_following) await api.delete(`/follow/${u.id}`); else await api.post(`/follow/${u.id}`);
      setU((x) => ({ ...x, is_following: !x.is_following, follower_count: (x.follower_count || 0) + (x.is_following ? -1 : 1) }));
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const message = async () => {
    if (!me) return onSignin();
    try { const { data } = await api.post("/conversations", { user_id: u.id }); onMessage({ ...data, other: data.other || u }); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#050a18] mx-auto max-w-[512px] flex flex-col animate-rise" data-testid="sl-user-profile">
      <div className="flex items-center gap-2 px-3 h-12 border-b border-white/10 shrink-0">
        <button onClick={onClose} data-testid="sl-user-profile-close"><X size={20} /></button>
        <div className="font-semibold text-sm truncate">@{username}</div>
      </div>
      {!u ? <Loading /> : (
        <div className="flex-1 overflow-y-auto hide-scroll">
          <div className="h-24 bg-gradient-to-br from-eblue/30 to-gold/20" style={u.banner_url ? { backgroundImage: `url(${mediaUrl(u.banner_url)})`, backgroundSize: "cover" } : {}} />
          <div className="px-4 -mt-9">
            <Avatar src={u.avatar_url} name={u.display_name} size={72} ring />
            <div className="mt-2 font-display font-bold text-lg"><CreatorName user={u} /></div>
            <div className="text-xs text-muted-foreground">@{u.username}</div>
            {u.bio && <p className="text-sm mt-2 text-white/85 whitespace-pre-wrap">{u.bio}</p>}
            <div className="flex gap-4 mt-3 text-sm"><span data-testid="sl-user-followers"><b>{formatNum(u.follower_count || 0)}</b> <span className="text-muted-foreground">followers</span></span><span><b>{formatNum(u.following_count || 0)}</b> <span className="text-muted-foreground">following</span></span></div>
            {me?.id !== u.id && <div className="flex gap-2 mt-3">
              <button onClick={follow} disabled={busy} className={`flex-1 h-10 rounded-full text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${u.is_following ? "surface-2 border border-white/15" : "btn-gold text-black"}`} data-testid="sl-user-follow-btn">
                {u.is_following ? <><UserCheck size={15} />Following</> : <><UserPlus size={15} />Follow</>}</button>
              <button onClick={message} className="h-10 px-4 rounded-full surface-2 border border-white/15 text-sm flex items-center gap-1.5" data-testid="sl-user-message-btn"><MessageSquare size={15} />Message</button>
            </div>}
          </div>
          <div className="px-3 py-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 px-1"><Grid3x3 size={12} />Posts</div>
            {!posts ? <Loading /> : posts.length === 0 ? <Empty title="No posts yet" /> : posts.slice(0, 20).map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
